import initCarousel from '../../helpers/carousel';
import { cartApi, getTranslation } from '../../helpers';

// Upper bound on slides in the carousel, shared across every product in the cart. The
// per-product limit is this divided by the number of cart lines, so a five-item cart
// does not push fifty accessories into a 442px drawer.
const TOTAL_RECOMMENDATIONS = 10;

// Splide options for the drawer: one card at a time with the next one peeking, so the
// arrows read as "there is more here" without a second card ever being fully cut off.
const carouselOptions = {
  perPage: 1,
  perMove: 1,
  gap: '0.5rem',
  pagination: false,
  arrows: true,
  padding: { right: '30%' },
};

const selectors = {
  crossSell: '[data-cart-cross-sell]',
  crossSellItem: '[data-cross-sell-item]',
  crossSellSlides: '[data-cross-sell-slides]',
  addButton: '[data-accessory-add]',
};

/**
 * The mini cart's "Add Accessories" carousel.
 *
 * Shopify's recommendations endpoint only takes one product at a time, so this requests
 * `main-cart-cross-sell` once per product in the cart with `intent=complementary` and merges
 * the rendered slides. Complementary products are curated per product in the Search &
 * Discovery app — there is deliberately no fallback to `intent=related`, so the block hides
 * itself whenever nothing has been curated for anything in the cart.
 *
 * @export
 * @class CartCrossSell
 */
export default class CartCrossSell {
  /**
   * Creates an instance of CartCrossSell.
   *
   * @param {HTMLElement} el The minicart section root
   * @param {NodeList} cartItems The `[data-cart-item]` rows currently in the drawer
   * @param {Function} updateCart Re-renders the drawer from a cart API `sections` payload
   */
  constructor(el, cartItems, updateCart) {
    this.el = el.querySelector(selectors.crossSell);
    this.updateCart = updateCart;
    this.cartProductIds = Array.from(cartItems, item => item.dataset.productId);

    if (!this.el || this.cartProductIds.length === 0) {
      return;
    }

    this._init();
  }

  _init = async () => {
    const slides = await this._fetchSlides();

    if (slides.length === 0) {
      return;
    }

    this._renderSlides(slides);
  };

  /**
   * Request complementary products for every product in the cart in parallel, then flatten
   * the responses into one deduplicated slide list.
   *
   * @returns {Promise<Element[]>} Slide elements, capped at TOTAL_RECOMMENDATIONS
   */
  _fetchSlides = async () => {
    const limit = Math.max(
      1,
      Math.floor(TOTAL_RECOMMENDATIONS / this.cartProductIds.length),
    );

    const responses = await Promise.all(
      this.cartProductIds.map(productId =>
        this._fetchSection(productId, limit),
      ),
    );

    // Anything already in the cart is not an upsell, and two cart lines routinely share a
    // complementary product — both are filtered here rather than in Liquid, which only ever
    // sees one product's recommendations at a time.
    const seen = new Set(this.cartProductIds);
    const slides = [];

    for (const response of responses) {
      for (const slide of response) {
        const { productId } = slide.dataset;

        if (seen.has(productId)) {
          continue;
        }

        seen.add(productId);
        slides.push(slide);

        if (slides.length >= TOTAL_RECOMMENDATIONS) {
          return slides;
        }
      }
    }

    return slides;
  };

  /**
   * Fetch one product's complementary recommendations as rendered slide markup.
   *
   * @param {string} productId
   * @param {number} limit
   * @returns {Promise<Element[]>} Slide elements, empty when nothing is curated or the request fails
   */
  _fetchSection = async (productId, limit) => {
    const url = `${Shopify.routes.root}recommendations/products?section_id=main-cart-cross-sell&intent=complementary&product_id=${productId}&limit=${limit}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        return [];
      }

      const html = new DOMParser().parseFromString(
        await response.text(),
        'text/html',
      );

      return Array.from(html.querySelectorAll(selectors.crossSellItem));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return [];
    }
  };

  /**
   * Fill the track, reveal the block and mount the carousel. Revealing has to happen before
   * Splide mounts — it measures slide widths, and a `hidden` track measures as zero.
   *
   * @param {Element[]} slides
   */
  _renderSlides = slides => {
    const slidesEl = this.el.querySelector(selectors.crossSellSlides);

    if (!slidesEl) {
      return;
    }

    slidesEl.replaceChildren(...slides);
    this.el.hidden = false;

    initCarousel(this.el, carouselOptions, true);

    this.el.querySelectorAll(selectors.addButton).forEach(button => {
      button.addEventListener('click', this._onAdd);
    });
  };

  _onAdd = event => {
    const button = event.currentTarget;
    const { variantId } = button.dataset;

    if (!variantId || button.disabled) {
      return;
    }

    const defaultLabel = button.textContent.trim();
    button.disabled = true;
    button.textContent = getTranslation('products.adding');

    cartApi
      .add({ items: [{ id: variantId, quantity: 1 }] })
      .then(response => {
        if (response.error) {
          this._resetButton(button, defaultLabel);
          return;
        }

        // The drawer re-renders from this payload, which replaces the button along with
        // everything else — so there is no success label to restore.
        if (response.sections) {
          this.updateCart({ sections: response.sections });
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(error);
        this._resetButton(button, defaultLabel);
      });
  };

  _resetButton = (button, label) => {
    button.disabled = false;
    button.textContent = label;
  };
}
